'use client'

import { useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { AktuellCard } from '@/components/Aktuell/AktuellCard'
import {
  AKTUELL_CATEGORY_LABEL,
  AKTUELL_CATEGORY_ORDER,
  type AktuellCategory,
} from '@/components/Aktuell/categories'
import { cn } from '@/utilities/ui'
import type { AktuellListItem } from '@/utilities/getCachedAktuell'

type Props = {
  items: AktuellListItem[]
  showCategoryFilter: boolean
  initialLimit: number
}

const ALL = 'Alle'

function FilterButton({
  label,
  isActive,
  onClick,
}: {
  label: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        'px-3 py-2 font-ui transition-all duration-200 uppercase',
        isActive
          ? 'bg-brand text-text-white border border-border-brand'
          : 'text-text-brand border border-border-brand hover:bg-brand hover:text-text-white',
      )}
    >
      {label}
    </button>
  )
}

export function AktuellOverviewClient({ items, showCategoryFilter, initialLimit }: Props) {
  const [filter, setFilter] = useState<string>(ALL)
  const [visible, setVisible] = useState<number>(initialLimit)

  const categoriesPresent = useMemo<AktuellCategory[]>(() => {
    const present = new Set<AktuellCategory>()
    for (const i of items) present.add(i.category)
    return AKTUELL_CATEGORY_ORDER.filter((c) => present.has(c))
  }, [items])

  const filterOptions: string[] = [
    ALL,
    ...categoriesPresent.map((c) => AKTUELL_CATEGORY_LABEL[c]),
  ]

  const filtered = useMemo(() => {
    if (filter === ALL) return items
    return items.filter((i) => AKTUELL_CATEGORY_LABEL[i.category] === filter)
  }, [items, filter])

  const shown = filtered.slice(0, visible)
  const canLoadMore = filtered.length > visible

  return (
    <div className="flex flex-col gap-12">
      {showCategoryFilter && categoriesPresent.length > 0 && (
        <div className="mb-8">
          {/* Desktop: button row */}
          <div className="hidden lg:flex flex-wrap gap-4">
            {filterOptions.map((label) => (
              <FilterButton
                key={label}
                label={label}
                isActive={filter === label}
                onClick={() => {
                  setFilter(label)
                  setVisible(initialLimit)
                }}
              />
            ))}
          </div>

          {/* Mobile & Tablet: native select */}
          <div className="lg:hidden relative">
            <label htmlFor="aktuell-filter" className="sr-only">
              Kategorie filtern
            </label>
            <select
              id="aktuell-filter"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value)
                setVisible(initialLimit)
              }}
              className="w-full appearance-none bg-transparent border-b border-text-primary/30 py-3 pr-10 pl-0 font-ui text-base uppercase text-text-primary focus:outline-none focus:border-text-primary transition-colors"
            >
              {filterOptions.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronRight
              aria-hidden
              className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-5 w-5 rotate-90 text-text-primary/60"
            />
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <p className="body-base text-text-secondary">
          In dieser Kategorie sind noch keine Beiträge verfügbar.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {shown.map((item) => (
            <AktuellCard key={item.id} item={item} />
          ))}
        </div>
      )}

      {canLoadMore && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + initialLimit)}
            className="label-md border-2 border-text-primary text-text-primary px-8 py-4 hover:bg-text-primary hover:text-text-white transition-colors cursor-pointer"
          >
            Mehr laden
          </button>
        </div>
      )}
    </div>
  )
}
